import { Skeleton } from "@somethings-ui/web";
import { useCareAssessments } from "@/lib/api/care";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { formatCareDate } from "./care-primitives";

interface AssessmentResultsProps {
  menteeId: string;
  ids: string[];
}

export function AssessmentResults({ menteeId, ids }: AssessmentResultsProps) {
  const [open, setOpen] = useState(false);
  const count = ids.length;
  const label = `${open ? "Hide" : "View"} ${count === 1 ? "result" : "results"} (${count})`;
  const Chevron = open ? ChevronDown : ChevronRight;

  if (count === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 self-start font-body text-xs leading-4 text-[#1f1e1d]"
      >
        {label}
        <Chevron className="size-4" aria-hidden />
      </button>
      {open ? <ResultsTable menteeId={menteeId} ids={ids} /> : null}
    </div>
  );
}

function ResultsTable({ menteeId, ids }: AssessmentResultsProps) {
  const { data, isPending, isError } = useCareAssessments(menteeId, ids, true);

  if (isPending) return <Skeleton className="h-40 w-full rounded-2xl" />;
  if (isError || data.length === 0) {
    return (
      <p className="font-body text-sm text-text-secondary">
        Could not load assessment results.
      </p>
    );
  }

  const questions = data[0].answers.map((a) => a.question);

  return (
    <div className="overflow-x-auto rounded-xl bg-[#f6f4ef] px-6 py-3">
      <table className="w-full border-collapse font-body text-sm">
        <thead>
          <tr className="border-b border-[#dfddd8] text-left text-[#a0998d]">
            <th className="py-2 font-normal">Question</th>
            {data.map((result) => (
              <th
                key={result.taken_at}
                className="py-2 pl-4 text-right font-mono text-xs font-normal"
              >
                {formatCareDate(result.taken_at)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {questions.map((question, index) => (
            <tr key={index} className="border-b border-[#ece8e1]">
              <td className="py-2 text-text-primary">{question}</td>
              {data.map((result) => (
                <td
                  key={result.taken_at}
                  className="py-2 pl-4 text-right text-text-primary"
                >
                  {result.answers[index]?.answer ?? "—"}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td className="py-2 font-medium text-text-primary">Total score</td>
            {data.map((result) => (
              <td
                key={result.taken_at}
                className="py-2 pl-4 text-right font-medium text-text-primary"
              >
                {result.score}/{result.max_score}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
